import { useState, useCallback, useEffect } from "react";
import { adminFetch } from "../lib/api";

export type UserRole = "pending" | "user" | "admin";

export interface SSOUser {
  id: string;
  email: string;
  display_name: string;
  is_staff: boolean;
  is_superuser: boolean;
  role: UserRole;
  status: "active" | "pending";
  last_sso_login: string | null;
  created_at: string;
}

export function useUserAccess() {
  const [users, setUsers] = useState<SSOUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch("/api/v1/admin/auth/users/");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setUsers(await res.json());
    } catch {
      setError("Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const patch = useCallback(async (profileId: string, body: Record<string, unknown>) => {
    setActionLoading(profileId);
    try {
      const res = await adminFetch(`/api/v1/admin/auth/users/${profileId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated: SSOUser = await res.json();
      setUsers((prev) => prev.map((u) => (u.id === profileId ? updated : u)));
    } catch {
      setError("Failed to update user.");
    } finally {
      setActionLoading(null);
    }
  }, []);

  const setAccess = useCallback(
    (profileId: string, isStaff: boolean) => patch(profileId, { is_staff: isStaff }),
    [patch],
  );

  const setRole = useCallback(
    (profileId: string, role: "user" | "admin") => patch(profileId, { role }),
    [patch],
  );

  const addUser = useCallback(async (email: string, role: "user" | "admin"): Promise<string | null> => {
    try {
      const res = await adminFetch("/api/v1/admin/auth/users/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, role }),
      });
      if (res.status === 409) return "A user with this email already exists.";
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return (data.detail as string) ?? `HTTP ${res.status}`;
      }
      const created: SSOUser = await res.json();
      setUsers((prev) => [created, ...prev]);
      return null; // null = success
    } catch {
      return "Failed to add user.";
    }
  }, []);

  return { users, loading, error, actionLoading, setAccess, setRole, addUser, refetch: fetchUsers };
}
